// src/pages/document/DocumentAuditLogPage.tsx
// Auto-generated by corebuilder. Edit freely.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import {
  Card,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';

const ACTION_VARIANT: Record<string, 'default' | 'error' | 'primary' | 'secondary' | 'success' | 'warning'> = {
  CREATED: 'success',
  PUBLISHED: 'success',
  DELETED: 'secondary',
  ARCHIVED: 'secondary',
  REJECTED: 'error',
  UPDATED: 'warning',
  STATUS_CHANGED: 'warning',
  VERSION_CREATED: 'warning',
  SUBMITTED_FOR_APPROVAL: 'warning',
  APPROVED: 'success',
  RESTORED: 'warning',
  ACKNOWLEDGMENT_REQUESTED: 'warning',
  ACKNOWLEDGED: 'success',
  OWNER_CHANGED: 'warning',
  CATEGORY_CHANGED: 'warning',
  DOWNLOADED: 'default',
  VIEWED: 'default',
};

/**
 * Document audit log page.
 */
export const DocumentAuditLogPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useDocument();
  const { t } = useI18n();
  const [entries, setEntries] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadAuditLog = async () => {
      if (!id) return;
      setIsLoading(true);
      try {
        const response = await store.ListAuditLog('document', id);
        const logs = response?.logs?.logs;
        setEntries(Array.isArray(logs) ? logs : []);
      } catch {
        // Error is handled by store
      } finally {
        setIsLoading(false);
      }
    };
    loadAuditLog();
  }, [id]);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <nav className="text-sm mb-2">
          <Link to="/documents" className="text-muted-foreground hover:text-foreground">
            {t('document.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to={`/documents/${id}`} className="text-muted-foreground hover:text-foreground">
            {t('document.details')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('document.auditLog')}</span>
        </nav>
        <h1 className="text-2xl font-bold text-foreground">{t('document.auditLog')}</h1>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>Action</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Details</TableHead>
              <TableHead>Changes</TableHead>
              <TableHead>Created At</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : entries.length === 0 ? (
              <TableEmpty colSpan={5} message={t('document.noAuditEntries')} />
            ) : (
              entries.map((item: any) => (
                <TableRow key={item.entity?.id || item.id || Math.random()}>
                  <TableCell>
                    <Badge variant={ACTION_VARIANT[item.action] ?? 'secondary'}>
                      {String(item.action ?? '')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {String(item.actor?.id ?? item.actor ?? '')}
                  </TableCell>
                  <TableCell>
                    {String(item.details ?? '')}
                  </TableCell>
                  <TableCell>
                    {item.old_value || item.new_value ? (
                      <span className="text-sm">
                        <span className="text-muted-foreground">{String(item.old_value ?? '')}</span>
                        {item.old_value && item.new_value && (
                          <span className="mx-1 text-muted-foreground">&rarr;</span>
                        )}
                        <span className="text-foreground">{String(item.new_value ?? '')}</span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {String(item.created_at ?? '')}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default DocumentAuditLogPage;
